// Floating chart windows: a chart or a visualization from the chat opens here, as a window of its own
// that can be dragged by its title bar, resized from its corner, minimized, maximized and closed —
// several at once, staggered, the last one touched on top. They float over every page, so a chart
// stays in view while the person works elsewhere. A page opens one with openChartWindow(); the shell
// mounts <ChartWindows /> once.
import { BarChart3, Maximize2, Minimize2, Minus, X } from 'lucide-react';
import * as React from 'react';
import { createPortal } from 'react-dom';
import { getTopZ } from '@cortex/ui';
import { VizView } from './VizView.tsx';

export const OPEN_CHART_EVENT = 'cortex:open-chart';

export type ChartContent =
  | { kind: 'chart'; svg?: string; columns?: string[]; rows?: Record<string, unknown>[] }
  | { kind: 'viz'; code: string; data: unknown };

export interface ChartWindowRequest {
  title: string;
  content: ChartContent;
}

export const openChartWindow = (req: ChartWindowRequest) =>
  window.dispatchEvent(new CustomEvent<ChartWindowRequest>(OPEN_CHART_EVENT, { detail: req }));

const DEFAULT_W = 640;
const DEFAULT_H = 460;
const MIN_W = 400;
const MIN_H = 320;
const BAR = 40;
const STAGGER = 28;

interface Win extends ChartWindowRequest {
  id: number;
  x: number;
  y: number;
  w: number;
  h: number;
  z: number;
  minimized: boolean;
  maximized: boolean;
}

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(v, Math.max(lo, hi)));

function Body({ content, title }: { content: ChartContent; title: string }) {
  if (content.kind === 'viz')
    return <VizView testid="chart-window-viz" title={title} code={content.code} data={content.data} />;
  if (content.svg)
    return (
      <div
        data-testid="chart-window-svg"
        className="w-full h-full text-page-text [&_svg]:w-full [&_svg]:h-full"
        // The SVG was rendered by the api's analytics package from validated rows (escaped text).
        dangerouslySetInnerHTML={{ __html: content.svg }}
      />
    );
  const cols = content.columns ?? Object.keys(content.rows?.[0] ?? {});
  return (
    <table className="w-full text-xs" data-testid="chart-window-table">
      <thead className="sticky top-0 bg-card-bg">
        <tr>
          {cols.map((c) => (
            <th key={c} className="px-2 py-1 text-left font-medium text-page-muted">
              {c}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {(content.rows ?? []).map((r, i) => (
          <tr key={i} className="border-t border-divider">
            {cols.map((c) => (
              <td key={c} className="px-2 py-1">
                {String(r[c] ?? '')}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function ChartWindows() {
  const [wins, setWins] = React.useState<Win[]>([]);
  const seq = React.useRef(0);

  React.useEffect(() => {
    const on = (e: Event) => {
      const req = (e as CustomEvent<ChartWindowRequest>).detail;
      if (!req?.content) return;
      setWins((all) => {
        const n = all.length;
        const w = Math.min(DEFAULT_W, window.innerWidth - 32);
        const h = Math.min(DEFAULT_H, window.innerHeight - 32);
        const x = clamp(window.innerWidth - w - 96 - (n % 6) * STAGGER, 16, window.innerWidth - w - 16);
        const y = clamp(72 + (n % 6) * STAGGER, 16, window.innerHeight - h - 16);
        return [
          ...all,
          { ...req, id: ++seq.current, x, y, w, h, z: getTopZ(), minimized: false, maximized: false },
        ];
      });
    };
    window.addEventListener(OPEN_CHART_EVENT, on);
    return () => window.removeEventListener(OPEN_CHART_EVENT, on);
  }, []);

  const patch = (id: number, p: Partial<Win> | ((w: Win) => Partial<Win>)) =>
    setWins((all) => all.map((w) => (w.id === id ? { ...w, ...(typeof p === 'function' ? p(w) : p) } : w)));
  const front = (id: number) => patch(id, () => ({ z: getTopZ() }));

  const drag = (win: Win) => (e: React.PointerEvent) => {
    if (win.maximized || (e.target as HTMLElement).closest('button')) return;
    front(win.id);
    const from = { x: e.clientX, y: e.clientY, left: win.x, top: win.y };
    const move = (ev: PointerEvent) =>
      patch(win.id, {
        x: clamp(from.left + ev.clientX - from.x, 8 - win.w + 120, window.innerWidth - 120),
        y: clamp(from.top + ev.clientY - from.y, 0, window.innerHeight - BAR),
      });
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };
  const resize = (win: Win) => (e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    front(win.id);
    const from = { x: e.clientX, y: e.clientY, w: win.w, h: win.h };
    const move = (ev: PointerEvent) =>
      patch(win.id, {
        w: Math.max(MIN_W, from.w + ev.clientX - from.x),
        h: Math.max(MIN_H, from.h + ev.clientY - from.y),
      });
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  if (!wins.length) return null;
  return createPortal(
    <>
      {wins.map((win) => {
        const box = win.maximized
          ? { left: 16, top: 16, width: window.innerWidth - 32, height: window.innerHeight - 32 }
          : { left: win.x, top: win.y, width: win.w, height: win.minimized ? BAR : win.h };
        const btn =
          'flex h-7 w-7 cursor-pointer items-center justify-center rounded border-0 bg-transparent text-white/80 hover:bg-white/10';
        return (
          <div
            key={win.id}
            role="dialog"
            aria-label={win.title}
            data-testid="chart-window"
            data-window={win.id}
            data-minimized={win.minimized ? '1' : '0'}
            data-maximized={win.maximized ? '1' : '0'}
            onPointerDown={() => front(win.id)}
            className="fixed flex flex-col overflow-hidden rounded-xl border border-[#C8D9EC] bg-card-bg shadow-[0_8px_40px_0_var(--shadow-blue-md)]"
            style={{ ...box, zIndex: win.z }}
          >
            <div
              onPointerDown={drag(win)}
              data-testid="chart-window-bar"
              className="flex h-10 shrink-0 cursor-grab select-none items-center gap-2 bg-[#1e2a4a] px-3 active:cursor-grabbing"
            >
              <BarChart3 className="h-4 w-4 shrink-0 text-white/70" />
              <span
                data-testid="chart-window-title"
                className="mr-auto truncate text-sm font-medium text-white/90"
              >
                {win.title}
              </span>
              <button
                type="button"
                data-testid="chart-window-minimize"
                title={win.minimized ? 'Expand' : 'Minimize'}
                aria-label={win.minimized ? 'Expand' : 'Minimize'}
                onClick={() => patch(win.id, (w) => ({ minimized: !w.minimized, maximized: false }))}
                className={btn}
              >
                <Minus className="h-4 w-4" />
              </button>
              <button
                type="button"
                data-testid="chart-window-maximize"
                title={win.maximized ? 'Restore' : 'Maximize'}
                aria-label={win.maximized ? 'Restore' : 'Maximize'}
                onClick={() => patch(win.id, (w) => ({ maximized: !w.maximized, minimized: false }))}
                className={btn}
              >
                {win.maximized ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
              </button>
              <button
                type="button"
                data-testid="chart-window-close"
                title="Close"
                aria-label="Close"
                onClick={() => setWins((all) => all.filter((w) => w.id !== win.id))}
                className={btn}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            {!win.minimized && (
              <div className="relative flex-1 min-h-0 overflow-auto p-3" data-testid="chart-window-body">
                <Body content={win.content} title={win.title} />
                {!win.maximized && (
                  <div
                    data-testid="chart-window-resize"
                    onPointerDown={resize(win)}
                    title="Resize"
                    className="absolute bottom-0 right-0 h-4 w-4 cursor-se-resize"
                    style={{
                      background:
                        'linear-gradient(135deg, transparent 50%, var(--color-page-muted, #94a3b8) 50%, transparent 60%, transparent 70%, var(--color-page-muted, #94a3b8) 70%, transparent 80%)',
                    }}
                  />
                )}
              </div>
            )}
          </div>
        );
      })}
    </>,
    document.body,
  );
}
